import { useCallback, useMemo } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  OnboardingLiveItemFragment,
  OnboardingLiveListPeriod,
} from "../../gql/graphql.ts";
import { BasicTable } from "../../UI/Table/BasicTable.tsx";
import { EmptyTableMessage } from "../../UI/Table/EmptyTableMessage.tsx";
import {
  SortLabels,
  TableColumnHeader,
} from "../../UI/Table/TableColumnHeader.tsx";
import { compareAlphabetically } from "../../UI/Table/compareAlphabetically.ts";
import { uniqueSortedOptions } from "../../UI/Table/Filters/uniqueSortedOptions.ts";
import {
  checkboxListFilterProps,
  dateRangeFilterProps,
  numberRangeFilterProps,
  sortableColumnProps,
} from "../../UI/Table/tableColumnHeaderProps.tsx";
import { useAllUserGroups } from "../../UserGroup/useAllUserGroups.ts";
import { useOnboardingLiveConductors } from "../useOnboardingLiveConductors.ts";
import { formatSlot } from "../onboardingLiveTime.ts";
import {
  FEEDBACK_STATES,
  feedbackStateLabels,
  feedbackStateOf,
  matchesOnboardingLivesFilters,
  OnboardingLivesFilters,
  OnboardingLivesSort,
  OnboardingLivesSortField,
} from "./onboardingLivesTableState.ts";
import { OnboardingLiveFeedbackBadge } from "./OnboardingLiveFeedbackBadge.tsx";
import {
  ALPHABETICAL_SORT_LABELS,
  COUNT_SORT_LABELS,
  DATE_SORT_LABELS,
} from "./onboardingLivesSortLabels.ts";

const FEEDBACK_SORT_LABELS: SortLabels = {
  asc: "Mancante → Inviato",
  desc: "Inviato → Mancante",
};

const FEEDBACK_OPTIONS = FEEDBACK_STATES.map((state) => ({
  value: state,
  label: feedbackStateLabels[state],
}));

type OnboardingLiveRow = {
  live: OnboardingLiveItemFragment;
  userGroupName: string;
  conductorName: string;
};

type Props = {
  lives: OnboardingLiveItemFragment[];
  period: OnboardingLiveListPeriod;
  sort: OnboardingLivesSort;
  onSortChange: (sort: OnboardingLivesSort) => void;
  filters: OnboardingLivesFilters;
  onFiltersChange: (filters: OnboardingLivesFilters) => void;
  onOpen: (live: OnboardingLiveItemFragment) => void;
};

export function OnboardingLivesTable({
  lives,
  period,
  sort,
  onSortChange,
  filters,
  onFiltersChange,
  onOpen,
}: Props) {
  const { userGroups } = useAllUserGroups();
  const { teacherNameOf } = useOnboardingLiveConductors();
  const userGroupNameOf = useCallback(
    (userGroupId: string) =>
      userGroups.find((g) => g.id === userGroupId)?.name ?? userGroupId,
    [userGroups],
  );
  const visibleRows = useMemo(
    () =>
      lives
        .filter((live) => matchesOnboardingLivesFilters(live, filters))
        .map((live) => ({
          live,
          userGroupName: userGroupNameOf(live.userGroupId),
          conductorName: teacherNameOf(live.teacherId),
        })),
    [lives, filters, userGroupNameOf, teacherNameOf],
  );
  const columns = useMemo(() => {
    const userGroupOptions = uniqueSortedOptions(
      lives.map((live) => ({
        value: live.userGroupId,
        label: userGroupNameOf(live.userGroupId),
      })),
    );
    const conductorOptions = uniqueSortedOptions(
      lives.map((live) => ({
        value: live.teacherId,
        label: teacherNameOf(live.teacherId),
      })),
    );
    const updateFilters = (changes: Partial<OnboardingLivesFilters>) =>
      onFiltersChange({ ...filters, ...changes });
    const sortableBy = (
      field: OnboardingLivesSortField,
      sortLabels: SortLabels,
    ) => sortableColumnProps({ field, sort, onSortChange, sortLabels });

    const columnHelper = createColumnHelper<OnboardingLiveRow>();
    return [
      columnHelper.accessor((row) => row.live.startUtc, {
        id: "startUtc",
        sortingFn: "basic",
        header: () => (
          <TableColumnHeader
            label="Data e ora"
            {...sortableBy("startUtc", DATE_SORT_LABELS)}
            {...dateRangeFilterProps(filters.date, (date) =>
              updateFilters({ date }),
            )}
          />
        ),
        cell: (info) => (
          <span className={"font-medium whitespace-nowrap"}>
            {formatSlot(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor("userGroupName", {
        id: "userGroup",
        sortingFn: compareAlphabetically,
        header: () => (
          <TableColumnHeader
            label="User group"
            {...sortableBy("userGroup", ALPHABETICAL_SORT_LABELS)}
            {...checkboxListFilterProps(
              userGroupOptions,
              filters.userGroupIds,
              (userGroupIds) => updateFilters({ userGroupIds }),
            )}
          />
        ),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("conductorName", {
        id: "conductor",
        sortingFn: compareAlphabetically,
        header: () => (
          <TableColumnHeader
            label="Conduttore"
            {...sortableBy("conductor", ALPHABETICAL_SORT_LABELS)}
            {...checkboxListFilterProps(
              conductorOptions,
              filters.teacherIds,
              (teacherIds) => updateFilters({ teacherIds }),
            )}
          />
        ),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor((row) => row.live.participantsCount, {
        id: "enrolled",
        header: () => (
          <TableColumnHeader
            label="Iscritti"
            {...sortableBy("enrolled", COUNT_SORT_LABELS)}
            {...numberRangeFilterProps(filters.enrolled, (enrolled) =>
              updateFilters({ enrolled }),
            )}
          />
        ),
        cell: (info) =>
          `${info.getValue()} / ${info.row.original.live.capacity}`,
      }),
      ...(period === OnboardingLiveListPeriod.Past
        ? [
            columnHelper.accessor(
              (row) => row.live.feedback?.participantsCount ?? undefined,
              {
                id: "attendees",
                sortUndefined: "last",
                header: () => (
                  <TableColumnHeader
                    label="Partecipanti (feedback)"
                    {...sortableBy("attendees", COUNT_SORT_LABELS)}
                    {...numberRangeFilterProps(filters.attendees, (attendees) =>
                      updateFilters({ attendees }),
                    )}
                  />
                ),
                cell: (info) => info.getValue() ?? "-",
              },
            ),
            columnHelper.accessor((row) => feedbackStateOf(row.live), {
              id: "feedback",
              sortingFn: "basic",
              header: () => (
                <TableColumnHeader
                  label="Feedback"
                  {...sortableBy("feedback", FEEDBACK_SORT_LABELS)}
                  {...checkboxListFilterProps(
                    FEEDBACK_OPTIONS,
                    filters.feedbackStates,
                    (feedbackStates) => updateFilters({ feedbackStates }),
                  )}
                />
              ),
              cell: (info) => (
                <OnboardingLiveFeedbackBadge state={info.getValue()} />
              ),
            }),
          ]
        : []),
    ];
  }, [
    lives,
    period,
    sort,
    onSortChange,
    filters,
    onFiltersChange,
    userGroupNameOf,
    teacherNameOf,
  ]);

  const sorting = useMemo(
    () => [{ id: sort.sortBy, desc: sort.sortDirection === "desc" }],
    [sort],
  );

  const table = useReactTable({
    columns,
    data: visibleRows,
    state: { sorting },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (lives.length <= 0)
    return <EmptyTableMessage message={"Nessuna live onboarding."} />;

  return (
    <>
      <BasicTable
        table={table}
        onRowClick={(row) => onOpen(row.live)}
        scrollableMinWidthClassName={"min-w-[720px]"}
      />
      {visibleRows.length <= 0 ? (
        <EmptyTableMessage message={"Nessuna live corrisponde ai filtri."} />
      ) : null}
    </>
  );
}
